import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, User, Lock, CreditCard, LogOut, Gift, Tag, ArrowUpCircle, ArrowDownCircle, XCircle, AlertTriangle, Edit2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import api from '../utils/api';
import { SPORTS_CONFIG, ENABLED_SPORTS } from '../config/sports';

// Sports limit per plan
const PLAN_SPORTS_LIMITS = {
  '2_sports_monthly': 2,
  '2_sports_yearly': 2,
  '4_sports_monthly': 4,
  '4_sports_yearly': 4,
  'all_sports_monthly': 6,
  'all_sports_yearly': 6,
};

// Plan pricing info (matches production prices)
const PLAN_INFO = {
  '2_sports_monthly': { name: '2 Sports', price: 2.99, cycle: 'monthly' },
  '2_sports_yearly': { name: '2 Sports', price: 14.99, cycle: 'yearly' },
  '4_sports_monthly': { name: '4 Sports', price: 4.99, cycle: 'monthly' },
  '4_sports_yearly': { name: '4 Sports', price: 16.99, cycle: 'yearly' },
  'all_sports_monthly': { name: 'All Sports', price: 6.99, cycle: 'monthly' },
  'all_sports_yearly': { name: 'All Sports', price: 19.99, cycle: 'yearly' },
};

export default function Account() {
  const navigate = useNavigate();
  const { user, logout, changePassword, checkSubscription, subscriptionStatus, refreshUser } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSportsModal, setShowSportsModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedSportsEdit, setSelectedSportsEdit] = useState([]);
  const [downgradeSelectedSports, setDowngradeSelectedSports] = useState([]);
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkSubscription();
  }, []);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) {
      toast.error('Please enter a code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/redeem-code', { code: redeemCode.trim() });
      toast.success(response.data.message);
      setShowRedeemModal(false);
      setRedeemCode('');
      await refreshUser();
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    try {
      await api.post('/subscription/cancel');
      toast.success('Subscription cancelled. You\'ll have access until the expiration date.');
      setShowCancelModal(false);
      await refreshUser();
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!selectedDowngradePlan || downgradeSelectedSports.length === 0) {
      toast.error('Please select a plan and sports');
      return;
    }
    
    const maxSports = PLAN_SPORTS_LIMITS[selectedDowngradePlan] || 2;
    if (downgradeSelectedSports.length > maxSports) {
      toast.error(`Selected plan only allows ${maxSports} sports`);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.post('/subscription/downgrade', {
        new_plan: selectedDowngradePlan,
        selected_sports: downgradeSelectedSports
      });
      toast.success(response.data.message);
      setShowDowngradeModal(false);
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule downgrade');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPendingDowngrade = async () => {
    setIsLoading(true);
    try {
      await api.delete('/subscription/cancel-downgrade');
      toast.success('Pending downgrade cancelled');
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel downgrade');
    } finally {
      setIsLoading(false);
    }
  };

  const openDowngradeModal = () => {
    // Set defaults based on current plan
    const currentLimit = getSportsLimit();
    const currentSports = subscriptionStatus?.selected_sports || [];
    const currentPlan = subscriptionStatus?.subscription_type || '';
    const isYearly = currentPlan.includes('yearly');
    
    if (currentLimit === 6) {
      // All sports -> can downgrade to 4 or 2
      setSelectedDowngradePlan(isYearly ? '4_sports_yearly' : '4_sports_monthly');
      setDowngradeSelectedSports(currentSports.slice(0, 4));
    } else if (currentLimit === 4) {
      // 4 sports -> can downgrade to 2
      setSelectedDowngradePlan(isYearly ? '2_sports_yearly' : '2_sports_monthly');
      setDowngradeSelectedSports(currentSports.slice(0, 2));
    }
    setShowDowngradeModal(true);
  };

  const handleToggleDowngradeSport = (sport) => {
    const maxSports = PLAN_SPORTS_LIMITS[selectedDowngradePlan] || 2;
    if (downgradeSelectedSports.includes(sport)) {
      setDowngradeSelectedSports(downgradeSelectedSports.filter(s => s !== sport));
    } else if (downgradeSelectedSports.length < maxSports) {
      setDowngradeSelectedSports([...downgradeSelectedSports, sport]);
    } else {
      toast.error(`You can only select ${maxSports} sports for this plan`);
    }
  };

  // Get sports emojis for display
  const getSelectedSportsDisplay = () => {
    const sports = subscriptionStatus?.selected_sports || [];
    if (sports.length === 0) return null;
    return sports.map(s => SPORTS_CONFIG[s]?.emoji).join(' ');
  };

  const getSportsLimit = () => {
    const planType = subscriptionStatus?.subscription_type;
    return PLAN_SPORTS_LIMITS[planType] || 6;
  };

  const handleOpenSportsModal = () => {
    setSelectedSportsEdit(subscriptionStatus?.selected_sports || []);
    setShowSportsModal(true);
  };

  const handleToggleSportEdit = (sport) => {
    const limit = getSportsLimit();
    setSelectedSportsEdit(prev => {
      if (prev.includes(sport)) {
        return prev.filter(s => s !== sport);
      }
      if (prev.length >= limit) {
        toast.error(`Your plan allows only ${limit} sports`);
        return prev;
      }
      return [...prev, sport];
    });
  };

  const handleSaveSports = async () => {
    const limit = getSportsLimit();
    if (selectedSportsEdit.length === 0) {
      toast.error('Please select at least one sport');
      return;
    }
    if (selectedSportsEdit.length > limit) {
      toast.error(`Your plan allows only ${limit} sports`);
      return;
    }

    setIsLoading(true);
    try {
      await api.put('/subscription/sports', { selected_sports: selectedSportsEdit });
      toast.success('Sports updated!');
      setShowSportsModal(false);
      await refreshUser();
      await checkSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sports');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000] pb-8">
      {/* Header */}
      <div className="p-4 flex items-center">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/athletes')}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
      </div>

      <div className="px-4">
        {/* Profile Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-[#00E5E5] flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-xl font-bold text-white">{user?.email}</h1>
          <p className="text-gray-400 text-sm">
            Member since {user?.created_at ? format(new Date(user.created_at), 'MMM yyyy') : 'N/A'}
          </p>
        </div>

        {/* Subscription Status */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <CreditCard className="w-5 h-5 text-[#00E5E5]" />
            <h2 className="text-white font-semibold">Subscription</h2>
          </div>
          
          {subscriptionStatus?.is_subscribed ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-[#4ADE80] text-black text-xs font-bold px-2 py-1 rounded">ACTIVE</span>
                <span className="text-white">{subscriptionStatus.plan_details?.name || 'Premium'}</span>
              </div>
              
              {/* Selected Sports */}
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Your Sports ({subscriptionStatus?.selected_sports?.length || 0}/{getSportsLimit()})</p>
                  <button
                    onClick={handleOpenSportsModal}
                    className="text-xs text-[#00E5E5] flex items-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    {(subscriptionStatus?.selected_sports?.length || 0) === 0 ? 'Select' : 'Change'}
                  </button>
                </div>
                {getSelectedSportsDisplay() ? (
                  <p className="text-2xl mt-1">{getSelectedSportsDisplay()}</p>
                ) : (
                  <button
                    onClick={handleOpenSportsModal}
                    className="mt-2 w-full bg-[#FF9500]/20 text-[#FF9500] font-semibold py-2 rounded-lg text-sm"
                  >
                    ⚠️ Select Your Sports to Start Tracking
                  </button>
                )}
              </div>
              
              <p className="text-sm text-gray-400 mb-4">
                Expires: {subscriptionStatus.subscription_expires 
                  ? format(new Date(subscriptionStatus.subscription_expires), 'MMM d, yyyy')
                  : 'N/A'}
              </p>
              
              {/* Pending Downgrade Notice */}
              {subscriptionStatus?.pending_downgrade && (
                <div className="bg-[#FF9500]/20 border border-[#FF9500]/30 rounded-xl p-3 mb-4">
                  <p className="text-[#FF9500] text-sm font-semibold mb-1">
                    📅 Downgrade Scheduled
                  </p>
                  <p className="text-gray-400 text-xs">
                    Your plan will change to {PLAN_INFO[subscriptionStatus.pending_downgrade.new_plan]?.name || 'a lower plan'} on your next billing date.
                  </p>
                  <button
                    onClick={handleCancelPendingDowngrade}
                    className="mt-2 text-xs text-[#00E5E5] underline"
                  >
                    Cancel Downgrade
                  </button>
                </div>
              )}
              
              {/* Upgrade, Downgrade & Cancel Buttons */}
              <div className="space-y-2">
                <button
                  data-testid="upgrade-btn"
                  onClick={() => navigate('/paywall')}
                  className="w-full bg-[#00E5E5]/20 text-[#00E5E5] font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-[#00E5E5]/30"
                >
                  <ArrowUpCircle className="w-4 h-4" />
                  Upgrade Plan
                </button>
                
                {/* Show downgrade option only for 4+ sports plans */}
                {getSportsLimit() > 2 && !subscriptionStatus?.pending_downgrade && (
                  <button
                    data-testid="downgrade-btn"
                    onClick={openDowngradeModal}
                    className="w-full bg-[#1A1A1A] text-gray-300 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-[#333]"
                  >
                    <ArrowDownCircle className="w-4 h-4" />
                    Downgrade Plan
                  </button>
                )}
                
                <button
                  data-testid="cancel-subscription-btn"
                  onClick={() => setShowCancelModal(true)}
                  className="w-full bg-[#1A1A1A] text-gray-400 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-[#333]"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Subscription
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-400 mb-2">
                Free Trial: {subscriptionStatus?.games_remaining || 0} games remaining
              </p>
              <button
                onClick={() => navigate('/paywall')}
                className="w-full bg-[#00E5E5] text-black font-semibold py-2 rounded-xl"
              >
                Upgrade Now
              </button>
            </div>
          )}
        </div>

        {/* Usage Stats */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-4">
          <h2 className="text-white font-semibold mb-3">Usage</h2>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-[#00E5E5]">{user?.games_used || 0}</p>
              <p className="text-sm text-gray-400">Games Tracked</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#FF9500]">
                {subscriptionStatus?.is_subscribed ? '∞' : subscriptionStatus?.games_remaining || 0}
              </p>
              <p className="text-sm text-gray-400">Remaining</p>
            </div>
          </div>
        </div>

        {/* Redeem Code Section */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-3 mb-2">
            <Tag className="w-5 h-5 text-[#00E5E5]" />
            <h2 className="text-white font-semibold">Have a Code?</h2>
          </div>
          <p className="text-gray-400 text-sm mb-3">
            Enter your promo or discount code
          </p>
          <button
            data-testid="redeem-code-btn"
            onClick={() => setShowRedeemModal(true)}
            className="w-full bg-[#00E5E5] text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
          >
            <Tag className="w-4 h-4" />
            Redeem Code
          </button>
        </div>

        {/* Settings */}
        <div className="space-y-2">
          <button
            data-testid="change-password-btn"
            onClick={() => setShowPasswordModal(true)}
            className="w-full bg-[#1A1A1A] rounded-2xl p-4 flex items-center gap-3 hover:bg-[#252525] transition-colors"
          >
            <Lock className="w-5 h-5 text-gray-400" />
            <span className="text-white">Change Password</span>
          </button>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full bg-[#1A1A1A] rounded-2xl p-4 flex items-center gap-3 hover:bg-[#252525] transition-colors"
          >
            <LogOut className="w-5 h-5 text-red-400" />
            <span className="text-red-400">Log Out</span>
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Change Password</h2>
            
            <div className="space-y-3">
              <input
                type="password"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
              />
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={isLoading}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redeem Code Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="text-center mb-4">
              <Tag className="w-12 h-12 text-[#00E5E5] mx-auto mb-2" />
              <h2 className="text-xl font-bold text-white">Redeem Code</h2>
              <p className="text-gray-400 text-sm">Enter your promo or discount code</p>
            </div>
            
            <input
              type="text"
              placeholder="Enter code (e.g., SAVE20)"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              className="w-full bg-[#000] text-white text-center text-xl font-bold tracking-widest rounded-xl py-4 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5] mb-4"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRedeemModal(false);
                  setRedeemCode('');
                }}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleRedeemCode}
                disabled={isLoading || !redeemCode.trim()}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {isLoading ? 'Redeeming...' : 'Redeem'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Cancel Subscription?</h2>
              <p className="text-gray-400 text-sm mt-2">
                Are you sure you want to cancel? You'll still have access until{' '}
                <span className="text-white font-semibold">
                  {subscriptionStatus?.subscription_expires 
                    ? format(new Date(subscriptionStatus.subscription_expires), 'MMM d, yyyy')
                    : 'your expiration date'}
                </span>
              </p>
            </div>
            
            <div className="bg-[#000] rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-400">
                After cancellation:
              </p>
              <ul className="text-sm text-gray-300 mt-2 space-y-1">
                <li>• Your data will be preserved</li>
                <li>• You can resubscribe anytime</li>
                <li>• Free trial games won't be restored</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Keep Subscription
              </button>
              <button
                data-testid="confirm-cancel-btn"
                onClick={handleCancelSubscription}
                disabled={isLoading}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {isLoading ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Sports Modal */}
      {showSportsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-white">Select Your Sports</h2>
              <p className="text-gray-400 text-sm mt-1">
                Choose up to {getSportsLimit()} sports for your plan
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 mb-6">
              {ENABLED_SPORTS.map(sport => {
                const config = SPORTS_CONFIG[sport];
                const isSelected = selectedSportsEdit.includes(sport);
                return (
                  <button
                    key={sport}
                    onClick={() => handleToggleSportEdit(sport)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                        : 'border-transparent bg-[#000]'
                    }`}
                  >
                    <span className="text-2xl block">{config?.emoji}</span>
                    <span className={`text-xs ${isSelected ? 'text-[#00E5E5]' : 'text-gray-400'}`}>
                      {config?.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="text-center text-sm text-gray-500 mb-4">
              {selectedSportsEdit.length}/{getSportsLimit()} selected
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSportsModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSports}
                disabled={isLoading || selectedSportsEdit.length === 0}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Sports'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Modal */}
      {showDowngradeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-2">Downgrade Plan</h2>
            <p className="text-sm text-gray-400 mb-6">
              Your new plan will take effect on your next billing date. You'll keep your current access until then.
            </p>
            
            {/* Plan Selection */}
            <label className="text-sm text-gray-400 mb-2 block">Select New Plan</label>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {getSportsLimit() === 6 && (
                <>
                  <button
                    onClick={() => {
                      const isYearly = subscriptionStatus?.subscription_type?.includes('yearly');
                      setSelectedDowngradePlan(isYearly ? '4_sports_yearly' : '4_sports_monthly');
                      setDowngradeSelectedSports(downgradeSelectedSports.slice(0, 4));
                    }}
                    className={`p-3 rounded-xl border-2 text-center ${
                      selectedDowngradePlan?.includes('4_sports') 
                        ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                        : 'border-[#333] bg-[#000]'
                    }`}
                  >
                    <p className="text-white font-semibold">4 Sports</p>
                    <p className="text-xs text-gray-400">
                      ${subscriptionStatus?.subscription_type?.includes('yearly') ? `${PLAN_INFO['4_sports_yearly'].price}/yr` : `${PLAN_INFO['4_sports_monthly'].price}/mo`}
                    </p>
                  </button>
                  <button
                    onClick={() => {
                      const isYearly = subscriptionStatus?.subscription_type?.includes('yearly');
                      setSelectedDowngradePlan(isYearly ? '2_sports_yearly' : '2_sports_monthly');
                      setDowngradeSelectedSports(downgradeSelectedSports.slice(0, 2));
                    }}
                    className={`p-3 rounded-xl border-2 text-center ${
                      selectedDowngradePlan?.includes('2_sports') 
                        ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                        : 'border-[#333] bg-[#000]'
                    }`}
                  >
                    <p className="text-white font-semibold">2 Sports</p>
                    <p className="text-xs text-gray-400">
                      ${subscriptionStatus?.subscription_type?.includes('yearly') ? `${PLAN_INFO['2_sports_yearly'].price}/yr` : `${PLAN_INFO['2_sports_monthly'].price}/mo`}
                    </p>
                  </button>
                </>
              )}
              {getSportsLimit() === 4 && (
                <button
                  onClick={() => {
                    const isYearly = subscriptionStatus?.subscription_type?.includes('yearly');
                    setSelectedDowngradePlan(isYearly ? '2_sports_yearly' : '2_sports_monthly');
                    setDowngradeSelectedSports(downgradeSelectedSports.slice(0, 2));
                  }}
                  className={`p-3 rounded-xl border-2 text-center col-span-2 ${
                    selectedDowngradePlan?.includes('2_sports') 
                      ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                      : 'border-[#333] bg-[#000]'
                  }`}
                >
                  <p className="text-white font-semibold">2 Sports</p>
                  <p className="text-xs text-gray-400">
                    ${subscriptionStatus?.subscription_type?.includes('yearly') ? `${PLAN_INFO['2_sports_yearly'].price}/yr` : `${PLAN_INFO['2_sports_monthly'].price}/mo`}
                  </p>
                </button>
              )}
            </div>
            
            {/* Sports Selection for Downgrade */}
            <label className="text-sm text-gray-400 mb-2 block">
              Select {PLAN_SPORTS_LIMITS[selectedDowngradePlan] || 2} Sports to Keep
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {ENABLED_SPORTS.map(sport => {
                const config = SPORTS_CONFIG[sport];
                const isSelected = downgradeSelectedSports.includes(sport);
                const maxSports = PLAN_SPORTS_LIMITS[selectedDowngradePlan] || 2;
                const isDisabled = !isSelected && downgradeSelectedSports.length >= maxSports;
                
                return (
                  <button
                    key={sport}
                    onClick={() => handleToggleDowngradeSport(sport)}
                    disabled={isDisabled}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? 'border-[#00E5E5] bg-[#00E5E5]/10' 
                        : isDisabled
                          ? 'border-transparent bg-[#000] opacity-40'
                          : 'border-transparent bg-[#000]'
                    }`}
                  >
                    <span className="text-2xl block">{config?.emoji}</span>
                    <span className={`text-xs ${isSelected ? 'text-[#00E5E5]' : 'text-gray-400'}`}>
                      {config?.name}
                    </span>
                  </button>
                );
              })}
            </div>
            
            <p className="text-center text-sm text-gray-500 mb-4">
              {downgradeSelectedSports.length}/{PLAN_SPORTS_LIMITS[selectedDowngradePlan] || 2} selected
            </p>
            
            <div className="bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-xl p-3 mb-4">
              <p className="text-[#FF9500] text-xs">
                ⚠️ You will lose access to sports not selected when downgrade takes effect. Any games recorded in those sports will still be saved.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDowngradeModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleDowngrade}
                disabled={isLoading || downgradeSelectedSports.length === 0}
                className="flex-1 bg-[#FF9500] text-black font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Schedule Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
